import { useState, useRef, useCallback } from 'react';
import { uploadFile, deleteUploadedFile } from '@/utils/apiClient';
import { saveLogoUrl, resetLogo, DEFAULT_LOGO, initLogoFromCache } from '@/hooks/useLogoUrl';
import useLogoUrl from '@/hooks/useLogoUrl';
import { fetchAllSiteData } from '@/utils/apiClient';

export default function LogoEditor() {
  const currentLogo = useLogoUrl();
  const [mode, setMode] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState('');
  const [urlPreview, setUrlPreview] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<{ file: File; objectUrl: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const isCustom = currentLogo !== DEFAULT_LOGO;

  const handleFile = (file: File) => {
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Lütfen geçerli bir görsel dosyası seçin (PNG, JPG, SVG, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Dosya boyutu 10 MB\'dan küçük olmalıdır.');
      return;
    }
    if (uploadPreview) URL.revokeObjectURL(uploadPreview.objectUrl);
    setUploadPreview({ file, objectUrl: URL.createObjectURL(file) });
    setSaved(false);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  const handleUrlChange = (val: string) => {
    setUrlInput(val);
    setUrlPreview(val.trim());
    setSaved(false);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (mode === 'upload' && uploadPreview) {
        const result = await uploadFile(uploadPreview.file);
        await saveLogoUrl(result.url, result.id);
        setSaved(true);
        setUploadPreview(null);
      } else if (mode === 'url' && urlPreview) {
        await saveLogoUrl(urlPreview);
        setSaved(true);
        setUrlInput('');
        setUrlPreview('');
      } else {
        setError('Kaydetmek için bir görsel seçin veya URL girin.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydetme sırasında bir hata oluştu.');
    }
    setSaving(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = async () => {
    try {
      await resetLogo();
      setUploadPreview(null);
      setUrlInput('');
      setUrlPreview('');
      setResetConfirm(false);
      setSaved(false);
    } catch {
      setError('Sıfırlama sırasında hata oluştu.');
    }
  };

  const previewSrc = mode === 'upload' ? (uploadPreview?.objectUrl || currentLogo) : (urlPreview || currentLogo);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-[#2B3654]">
        <div>
          <h2 className="text-white font-semibold text-base">Logo & Marka Görseli</h2>
          <p className="text-[#7B8FAF] text-xs mt-1">Navbar, footer ve yönetim panelinde kullanılan logo</p>
        </div>
        {isCustom && !resetConfirm && (
          <button
            onClick={() => setResetConfirm(true)}
            className="px-4 py-2 text-sm text-[#7B8FAF] border border-[#2B3654] rounded-lg hover:border-red-500/40 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap"
          >
            Varsayılana Sıfırla
          </button>
        )}
        {resetConfirm && (
          <div className="flex items-center gap-2">
            <span className="text-red-400 text-xs">Emin misin?</span>
            <button onClick={handleReset} className="px-3 py-1.5 text-xs bg-red-500/20 border border-red-500/40 text-red-400 rounded-lg cursor-pointer hover:bg-red-500/30 transition-all whitespace-nowrap">
              Evet, Sıfırla
            </button>
            <button onClick={() => setResetConfirm(false)} className="px-3 py-1.5 text-xs text-[#7B8FAF] border border-[#2B3654] rounded-lg cursor-pointer hover:text-white transition-all whitespace-nowrap">
              İptal
            </button>
          </div>
        )}
      </div>

      {/* Live Preview */}
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-3">Canlı Önizleme</p>
        <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-6 flex flex-col sm:flex-row gap-6">
          <div className="flex-1 flex flex-col items-center gap-3">
            <div className="w-full bg-[#0A1020] border border-[#1E2B4A] rounded-lg flex items-center justify-center px-6 py-5">
              <img src={previewSrc} alt="Logo önizleme (koyu)" className="h-12 w-auto object-contain max-w-[200px]" onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_LOGO; }} />
            </div>
            <p className="text-[#465886] text-xs">Koyu arka plan (Navbar)</p>
          </div>
          <div className="flex-1 flex flex-col items-center gap-3">
            <div className="w-full bg-white border border-[#E2E8F0] rounded-lg flex items-center justify-center px-6 py-5">
              <img src={previewSrc} alt="Logo önizleme (açık)" className="h-12 w-auto object-contain max-w-[200px]" onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_LOGO; }} />
            </div>
            <p className="text-[#465886] text-xs">Açık arka plan</p>
          </div>
        </div>
      </div>

      {/* Mode Tabs */}
      <div>
        <div className="inline-flex items-center bg-[#0F1729] border border-[#2B3654] rounded-lg p-1 gap-1 mb-5">
          {[
            { key: 'upload', label: 'Dosya Yükle', icon: 'ri-upload-2-line' },
            { key: 'url', label: 'URL ile Ekle', icon: 'ri-link-m' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setMode(tab.key as 'upload' | 'url'); setError(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === tab.key ? 'bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40' : 'text-[#7B8FAF] hover:text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></div>
              {tab.label}
            </button>
          ))}
        </div>

        {mode === 'upload' && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-200 ${
              dragOver ? 'border-[#3B82F6] bg-[#3B82F6]/10' : uploadPreview ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-[#2B3654] hover:border-[#3B82F6]/50 hover:bg-[#3B82F6]/5'
            }`}
          >
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
            {uploadPreview ? (
              <div className="flex flex-col items-center gap-3">
                <img src={uploadPreview.objectUrl} alt="Seçilen logo" className="h-16 w-auto object-contain max-w-[200px] mx-auto" />
                <p className="text-emerald-400 text-sm font-medium">{uploadPreview.file.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-[#7B8FAF] text-xs">({(uploadPreview.file.size / 1024).toFixed(0)} KB) — değiştirmek için tekrar tıkla</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); URL.revokeObjectURL(uploadPreview.objectUrl); setUploadPreview(null); setSaved(false); }}
                    className="w-6 h-6 flex items-center justify-center bg-red-500/20 text-red-400 rounded-full hover:bg-red-500/40 cursor-pointer flex-shrink-0 transition-colors"
                    title="Seçimi iptal et"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 flex items-center justify-center bg-[#1E2B4A] rounded-full mx-auto mb-4">
                  <i className="ri-image-add-line text-2xl text-[#3B82F6]"></i>
                </div>
                <p className="text-white text-sm font-medium mb-1">Görseli sürükle veya tıklayarak seç</p>
                <p className="text-[#7B8FAF] text-xs">PNG, JPG, SVG, WebP — maks. 10 MB</p>
                <p className="text-[#465886] text-xs mt-2">Şeffaf arka planlı PNG önerilir</p>
              </>
            )}
          </div>
        )}

        {mode === 'url' && (
          <div className="space-y-3">
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest">Logo Görseli URL'si</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                <i className="ri-link-m text-sm"></i>
              </div>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg pl-9 pr-4 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
              />
            </div>
            <p className="text-[#465886] text-xs">CDN veya doğrudan görsel linki girin.</p>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-4 py-3 rounded-lg">
          <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
          {error}
        </div>
      )}

      {/* Tips */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5">
        <p className="text-white text-xs font-semibold mb-3 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-lightbulb-line text-amber-400"></i></div>
          Logo Önerileri
        </p>
        <ul className="space-y-1.5 text-[#7B8FAF] text-xs">
          <li className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-fill text-[4px] mt-2 text-[#3B82F6] flex-shrink-0"></i>Şeffaf arka planlı PNG formatı en iyi sonucu verir</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-fill text-[4px] mt-2 text-[#3B82F6] flex-shrink-0"></i>Tavsiye edilen boyut: en az 400×120 piksel (yatay format)</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-fill text-[4px] mt-2 text-[#3B82F6] flex-shrink-0"></i>Yüklenen görseller Natro sunucusuna kaydedilir</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-fill text-[4px] mt-2 text-[#3B82F6] flex-shrink-0"></i>Değişiklik kaydedildiğinde tüm sayfalarda anlık güncellenir</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || (!uploadPreview && !urlPreview)}
          className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
            saved ? 'bg-emerald-500 text-white' : 'bg-[#3B82F6] hover:bg-[#3d4e6a] text-white'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            {saving ? <i className="ri-loader-4-line animate-spin text-sm"></i> : saved ? <i className="ri-check-line text-sm"></i> : <i className="ri-save-line text-sm"></i>}
          </div>
          {saving ? 'Yükleniyor...' : saved ? 'Kaydedildi!' : 'Logoyu Kaydet'}
        </button>
      </div>
    </div>
  );
}
